import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Content,
  Pageable,
  QuestionTestCase,
} from "@src/pages/challenge-landing/topic-companies-details/types";

interface ResponseTestCase {
  input: string;
  expectedOutput: string | Record<string, string>;
  actualOutput: string;
  result: boolean;
  error: string;
  testCaseType: "EASY" | "MEDIUM" | "HARD";
}

export interface UserSubmission {
  status: string;
  penalty: number;
  duration: string;
  language: string;
}

export interface CodeResultResponse {
  testCases: ResponseTestCase[];
  compilationError: string;
  questionId: string;
  versionId: string;
  studentId: string;
  labels: string[];
}
export interface StudentAnswers {
  [key: string]: Record<string, string>;
}

export interface ChallengeDataType {
  name?: string;
  description?: string;
  challengeDtoList: Content[];
  paginationData: Pageable | null;
  source?: string;
  title?: string;
}

interface InitialState {
  challengeData: ChallengeDataType | null;
  selectedQuestion: Content | null;
  codeResult: CodeResultResponse | null;
  studentAnswers: StudentAnswers | null;
  currentQuestion: Content | null;
  defaultSelectedQuestion: Content | null;
  type: string;
  filters: Record<string, string[] | number[]>;
  page: number;
  allQuestionResponseList: Content[];
  userSubmissionData: UserSubmission[];
}

const initialState: InitialState = {
  challengeData: null,
  selectedQuestion: null,
  codeResult: null,
  studentAnswers: {},
  currentQuestion: null,
  defaultSelectedQuestion: null,
  type: "default",
  filters: {},
  page: 0,
  allQuestionResponseList: [],
  userSubmissionData: [],
};

const studentChallengeAssessmentSlice = createSlice({
  name: "studentChallengeAssessmentScreen",
  initialState,
  reducers: {
    setChallengeData: (state, action: PayloadAction<ChallengeDataType>) => {
      state.challengeData = { ...action.payload };
    },

    setSelectedQuestion: (state, action: PayloadAction<Content | null>) => {
      state.selectedQuestion = action.payload;
      // const selectedQuestion = action.payload?.version?.testCases?.map(
      //   testCase => ({
      //     ...testCase,
      //     disabled: true,
      //   })
      // );
    },

    addTestCase: (state) => {
      const testCases = state.selectedQuestion?.testCases;
      if (Array.isArray(testCases) && testCases.length > 0) {
        testCases.push({
          ...testCases[testCases.length - 1],
          disabled: false,
        } as QuestionTestCase & { disabled: boolean });
      }
    },

    setCodeResult: (state, action) => {
      state.codeResult = action.payload;
    },

    setInitialStudentAnswers: (state, action) => {
      state.studentAnswers = { ...state.studentAnswers, ...action.payload };
    },

    setStudentAnswers: (state, action) => {
      state.studentAnswers = action.payload;
    },
    setCurrentQuestionResponse: (state, action) => {
      state.currentQuestion = action.payload;
    },
    setDefaultSelectedQuestion: (state, action) => {
      state.defaultSelectedQuestion = action.payload;
    },
    setChallengeType: (state, action: PayloadAction<string>) => {
      state.type = action.payload;
    },
    setFilters: (
      state,
      action: PayloadAction<Record<string, string[] | number[]>>
    ) => {
      state.filters = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setAllQuestionResponseList: (state, action) => {
      const index = state.allQuestionResponseList?.findIndex(
        (q) => q.id === action.payload.id
      );

      if (index !== -1 && index !== undefined) {
        state.allQuestionResponseList[index] = action.payload;
      } else {
        state.allQuestionResponseList?.push(action.payload);
      }
    },
    setUserSubmissionData: (state, action) => {
      state.userSubmissionData = action.payload;
    },
  },
});

export const {
  setChallengeData,
  setSelectedQuestion,
  addTestCase,
  setCodeResult,
  setStudentAnswers,
  setInitialStudentAnswers,
  setCurrentQuestionResponse,
  setDefaultSelectedQuestion,
  setChallengeType,
  setFilters,
  setPage,
  setAllQuestionResponseList,
  setUserSubmissionData,
} = studentChallengeAssessmentSlice.actions;

export default studentChallengeAssessmentSlice.reducer;
